/**
 * Link builders
 */

import type { Event, Guide, Map, MapWaypoint, Place, PlaceType } from "../types";

function slugify(text?: string) {
  text = text == null ? "" : `${text}`;

  return text
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-") // Replace multiple - with single -
    .trim();
}

export type QueryParams = { [key: string]: string | null | undefined };

export function buildPath(path: string, query: QueryParams = {}): string {
  const params = new URLSearchParams();

  for (const key in query) {
    if (query[key] != null) {
      params.set(key, query[key]);
    }
  }

  const search = params.toString();

  if (search) {
    return path + "?" + search;
  }

  return path;
}

export function mapPath(query?: QueryParams) {
  return buildPath("/map", query);
}

export function mapShowPath(map: Partial<Map>, query?: QueryParams) {
  return buildPath(`/maps/${map.id}`, query);
}

export function placePath(place: Partial<Place>, query?: QueryParams) {
  const slug = slugify(place.title);

  return buildPath(`/places/${place.id}-${slug}`, query);
}

export function placeTypePath(placeType: Partial<PlaceType>, query?: QueryParams) {
  const slug = slugify(placeType.label);

  return buildPath(`/place-types/${placeType.id}-${slug}`, query);
}

export function eventPath(event: Partial<Event>, query?: QueryParams) {
  const slug = slugify(event.title);

  return buildPath(`/events/${event.id}-${slug}`, query);
}

export function schedulePath(query?: QueryParams) {
  return buildPath(`/schedule`, query);
}

export function placesPath(query?: QueryParams) {
  return buildPath(`/places`, query);
}

export function morePath(query?: QueryParams) {
  return buildPath(`/more`, query);
}

export function infoPath(query?: QueryParams) {
  return buildPath(`/info`, query);
}

export function homePath(query?: QueryParams) {
  return buildPath(`/home`, query);
}

/**
 * Returns URL for waypoint, including object existing URL search
 * parameters.
 */
export function waypointPath(waypoint: MapWaypoint): string {
  const url = new URL(window.location.href);

  if (waypoint.placeId) {
    url.searchParams.set("place", waypoint.placeId);
  } else {
    url.searchParams.delete("place");
  }

  // Delete event to allow proper navigation
  url.searchParams.delete("event");

  url.searchParams.set("waypoint", waypoint.id);

  return url.toString();
}

/**
 * Returns next possible path depending on given settings
 */

type MainPagePathOptions = {
  showHome?: boolean;
  showMap?: boolean;
  showSchedule?: boolean;
};

export function mainPagePath(settings: MainPagePathOptions): string {
  if (settings.showHome) {
    return homePath();
  }

  if (settings.showMap) {
    return mapPath();
  }

  if (settings.showSchedule) {
    return schedulePath();
  }

  return placesPath();
}

export function guidesPath(query?: QueryParams) {
  return buildPath(`/guides`, query);
}

export function guideChecklistPath(guide: Guide, query?: QueryParams) {
  const slug = slugify(guide.title);

  return buildPath(`/guides/${guide.id}-${slug}/checklist`, query);
}
